import React from 'react';
import { Badge } from '@/components/ui/badge';
import { 
  Users, 
  Wrench, 
  MapPin, 
  Calendar,
  Shield,
  Clock,
  DollarSign
} from 'lucide-react';
import HelicopterSilhouetteIcon from '@/components/icons/HelicopterSilhouetteIcon';
import { cn } from '@/lib/utils';

interface Service {
  type: string;
  included: boolean;
}

interface ServiceIconsDisplayProps {
  services?: Service[] | any;
  leaseType?: 'dry' | 'wet';
  crewQualifications?: string[];
  maintenanceResponsibility?: string;
  minimumLeasePeriod?: number;
  maximumLeasePeriod?: number;
  positioningCost?: string;
  className?: string;
}

const ServiceIconsDisplay: React.FC<ServiceIconsDisplayProps> = ({
  services,
  leaseType,
  crewQualifications,
  maintenanceResponsibility,
  minimumLeasePeriod,
  maximumLeasePeriod,
  positioningCost,
  className
}) => {
  // Parse services if it's a JSON object
  const servicesList = Array.isArray(services) 
    ? services 
    : (services && typeof services === 'object' ? Object.values(services) : []);

  const includedServices = servicesList.filter((s: any) => s.included || s === true);

  if (leaseType !== 'wet' && !includedServices.length) return null;

  return (
    <div className={cn("space-y-3", className)}>
      {/* Lease Type Header */}
      {leaseType === 'wet' && (
        <div className="flex items-center gap-2">
          <HelicopterSilhouetteIcon className="w-4 h-4" />
          <span className="text-sm font-semibold text-foreground">Wet Lease Services</span>
        </div>
      )}

      {/* Service Grid */}
      {includedServices.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {includedServices.map((service: any, idx: number) => {
            const serviceType = typeof service === 'object' ? service.type : service;
            const icon = getServiceIcon(serviceType);
            
            return (
              <div 
                key={idx} 
                className="flex items-center gap-2 p-2 rounded-lg bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800"
              >
                {icon}
                <span className="text-xs font-medium text-purple-900 dark:text-purple-300 truncate">
                  {formatServiceName(serviceType)}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Crew Qualifications */}
      {crewQualifications && crewQualifications.length > 0 && (
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Users className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
            <span className="text-xs font-medium text-muted-foreground uppercase">Crew Qualifications</span>
          </div>
          <div className="flex flex-wrap gap-1">
            {crewQualifications.map((qual, idx) => (
              <Badge key={idx} variant="secondary" className="text-xs bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800">
                {qual}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Maintenance Responsibility */}
      {maintenanceResponsibility && (
        <div className="flex items-center gap-2 p-2 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
          <Wrench className="w-3.5 h-3.5 text-green-600 dark:text-green-400 flex-shrink-0" />
          <div className="min-w-0">
            <div className="text-xs text-green-700 dark:text-green-300 font-medium">Maintenance</div>
            <div className="text-xs text-green-600 dark:text-green-400 truncate">{maintenanceResponsibility}</div>
          </div>
        </div>
      )}

      {/* Lease Period */}
      {(minimumLeasePeriod || maximumLeasePeriod) && (
        <div className="flex items-center gap-2 p-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
          <Calendar className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
          <div className="min-w-0">
            <div className="text-xs text-amber-700 dark:text-amber-300 font-medium">Lease Period</div>
            <div className="text-xs text-amber-600 dark:text-amber-400">
              {minimumLeasePeriod && maximumLeasePeriod 
                ? `${minimumLeasePeriod} - ${maximumLeasePeriod} months`
                : minimumLeasePeriod 
                  ? `Min ${minimumLeasePeriod} months`
                  : `Max ${maximumLeasePeriod} months`
              }
            </div>
          </div>
        </div>
      )}

      {/* Positioning Cost */}
      {positioningCost && (
        <div className="flex items-center gap-2 p-2 rounded-lg bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800">
          <DollarSign className="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
          <div className="min-w-0">
            <div className="text-xs text-indigo-700 dark:text-indigo-300 font-medium">Positioning</div>
            <div className="text-xs text-indigo-600 dark:text-indigo-400 truncate">{positioningCost}</div>
          </div>
        </div>
      )}
    </div>
  );
};

// Helper function to get service icon
const getServiceIcon = (serviceType: string) => {
  const iconClass = "w-3.5 h-3.5 text-purple-600 dark:text-purple-400 flex-shrink-0";
  
  switch (serviceType?.toLowerCase()) {
    case 'crew':
    case 'pilots':
      return <Users className={iconClass} />;
    case 'maintenance':
      return <Wrench className={iconClass} />;
    case 'insurance':
      return <Shield className={iconClass} />;
    case 'positioning':
      return <MapPin className={iconClass} />;
    default:
      return <HelicopterSilhouetteIcon className="w-3.5 h-3.5" />;
  }
};

// Helper function to format service name
const formatServiceName = (serviceType: string) => {
  if (!serviceType) return 'Service';
  return serviceType.charAt(0).toUpperCase() + serviceType.slice(1);
};

export default ServiceIconsDisplay;